import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { type PriceData } from "../../../models/PriceData";
import { type WeightData } from "../../../models/WeightData";
import { clearQuote, updateDate } from "../../redux/actions";
import { type CustomDispatch } from "../../../redux/types";
import PageToolbar from "../../../common/components/PageToolbar";
import OrderTotals from "~/feature/cart/cart/components/CartPage/OrderTotals";
import Spacer from "../../../common/components/Spacer";
import * as WeightUtils from "../../../utils/WeightUtils";
import * as PriceUtils from "../../../utils/PriceUtils";
import { connect, useSelector } from "react-redux";
import ProductTable from "./ProductTable/ProductTable";
import FullscreenLoader from "../../../common/components/FullscreenLoader";
import classNames from "classnames";
import QuoteOrderTotals from "./QuoteOrderTotals";
import { selectTargetSubtotal } from "../../redux/activeQuoteSelectors";
import ToReviewButton from "./ToReviewButton";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { type LineItem } from "~/feature/cart/cart/models/CartData";
import { type RootState } from "../../../redux/store";
import { selectSubtotal } from "~/feature/cart/cart/redux/selectors/productEntrySelectors";
import OptimeInfoPopup from "~/feature/cart/cart/components/CartPage/OptimeInfoPopup";
import ToastMessage from "../../../common/components/Messages/ToastMessage/ToastMessage";
import { ActiveQuotePageModeContext } from "../ActiveQuoteRootPage";
import { scrollToTop } from "../../../utils/misc";
import { Mode } from "~/feature/cart/cart/models/QuoteData";
import { sendInfoWanrSeenQuote } from "../../api/sendInfoWanrSeenQuote";
import {
  selectLineItemsByProduct,
  selectShowOptimePopupOnCheckout,
} from "~/feature/cart/cart/redux/selectors/cartSelectors";
import { useTranslation } from "react-i18next";
import { selectCart } from "~/feature/cart/cart/redux/selectors/groupedCartSelectors";
import { DownloadReportButton } from "~/feature/cart/cart/components/CartPage/Buttons/DownloadReportButton/DownloadReportButton";
import { ReportType } from "~/feature/cart/cart/components/CartPage/Buttons/DownloadReportButton/api/downloadCartReport";
import { selectIsPnaLoading } from "~/feature/cart/cart/redux/selectors/cartPageSelectors";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { Button } from "~/common/components/Button/Button";

interface FromStateProps {
  requestInFlight: boolean;
  accountNumber: string;
  totalWeight: WeightData;
  totalSum: PriceData;
  totalTargetSum: PriceData;
  entryIds: number[];
  validCart: boolean;
  dstError: boolean;
  showOptimePopupOnCheckout: boolean;
}

interface DispatchProps {
  updateDate: (date: Date, packageEntryId: number) => void;
  deleteAll: () => void;
}

type Props = DispatchProps & FromStateProps;

export const ActiveQuoteShowInternalContext = createContext(false);

const ActiveQuoteEdit = (props: Props) => {
  const {
    totalWeight,
    totalSum,
    totalTargetSum,
    validCart,
    entryIds,
    requestInFlight,
    deleteAll,
    dstError,
    showOptimePopupOnCheckout,
  } = props;
  const [showOptimeInfoPopup, setShowOptimeInfoPopup] = useState<boolean>(false);
  const [showDstError, setShowDstError] = useState<boolean>(false);
  const isPnaLoading = useSelector(selectIsPnaLoading);

  const { t } = useTranslation();

  useEffect(() => {
    setShowDstError(dstError);
    if (dstError) {
      sendInfoWanrSeenQuote();
    }
  }, [dstError]);

  const isEmpty = entryIds.length === 0;

  const { val: showInternal, toggle: toggleShowInternal } = useBooleanState(false);

  // Go to review
  const setMode = useContext(ActiveQuotePageModeContext);
  const cbGoToReview = useCallback(() => {
    setMode(Mode.REVIEW);
    scrollToTop();
  }, [setMode]);

  const ToReviewClickHandler = () => {
    showOptimePopupOnCheckout ? setShowOptimeInfoPopup(true) : cbGoToReview();
  };
  //

  const createQuoteButton = <ToReviewButton disabled={!validCart} clickHandler={ToReviewClickHandler} />;

  const cartData = useSelector(selectCart);

  const disableReportDownload = isPnaLoading;

  return (
    <ActiveQuoteShowInternalContext.Provider value={showInternal}>
      {!isEmpty && (
        <PageToolbar
          left={
            <Stacker gap={16} direction={"row"} className={"scha__active-quote__report-buttons"}>
              <span className="scha__order-list__download-title">{t("cartPage.downloads.title")}</span>
              <DownloadReportButton type={ReportType.CSV} disabled={disableReportDownload} />
              <DownloadReportButton type={ReportType.XLSX} disabled={disableReportDownload} />
            </Stacker>
          }
          right={createQuoteButton}
        />
      )}

      <div className={classNames("scha__active-quote", showInternal && "scha__active-quote--show-details")}>
        <ProductTable
          group={cartData.orderables}
          data-test-id={"active-quote-section-regular"}
          editMode={true}
          onClearCart={deleteAll}
          onToggleDetails={toggleShowInternal}
          showInternal={showInternal}
        />

        {!isEmpty && (
          <>
            <Spacer isThick={true} className={"mb-30 mt-60 p-relative"} />
            {/*<QuoteThickSpacer className={'pb-30 pt-60'} showInternal={showInternal}/>*/}
            <div className={classNames("d-flex flex-justify-end", !showInternal && "row")}>
              <div className="col-4">
                <OrderTotals
                  showPriceOnRequest={false}
                  weight={totalWeight}
                  totalValue={totalSum}
                  hideTaxesMsg={true}
                />
                <Spacer className={"mt-3 mb-3"} />
              </div>

              {showInternal && (
                <>
                  <div className={"scha__active-quote__total-target-price px-3"}>
                    <div className="scha__active-quote__total-target-price__bg" />
                    <QuoteOrderTotals className={"p-relative"} totalValue={totalTargetSum} />
                    <Spacer className={"mt-3 mb-3 p-relative"} />
                  </div>
                  <div className={"scha__active-quote__btn-col-spacer"} />
                </>
              )}
            </div>

            <div className="d-flex flex-justify-end mt-30">{createQuoteButton}</div>
          </>
        )}
      </div>

      {requestInFlight && <FullscreenLoader />}
      {showOptimeInfoPopup && (
        <OptimeInfoPopup
          onClose={() => setShowOptimeInfoPopup(false)}
          closeButton={
            <Button variant={"text"} onClick={() => setShowOptimeInfoPopup(false)}>
              {t("activeQuotePage.backToQuote")}
            </Button>
          }
          continueButton={<Button onClick={cbGoToReview}>{t("activeQuotePage.send")}</Button>}
        />
      )}
      {showDstError && (
        <ToastMessage type={"warning"} onClose={() => setShowDstError(false)}>
          {t("quotePage.warnMsg.dst")}
        </ToastMessage>
      )}
    </ActiveQuoteShowInternalContext.Provider>
  );
};

const mapStateToProps = (state: RootState): FromStateProps => {
  if (state.cartPage.accountNumber === "") {
    return {
      requestInFlight: false,
      accountNumber: "",
      totalWeight: WeightUtils.zero(),
      totalSum: PriceUtils.zero(),
      totalTargetSum: PriceUtils.zero(),
      entryIds: [],
      validCart: true,
      dstError: false,
      showOptimePopupOnCheckout: false,
    };
  }

  const { cart, cartPage, products } = state;
  const lineItemsByProduct = selectLineItemsByProduct(state);
  const allProductCodes = Object.keys(lineItemsByProduct);
  const totalsPerProduct = allProductCodes.map((productCode) =>
    selectSubtotal(state, {
      productCode,
      isInCheckout: false,
    }),
  );

  const packEntries: LineItem[] = Object.values(cart.packEntriesById);

  const totalPrice = PriceUtils.volatileSum(...totalsPerProduct);

  const targetTotalsPerProduct = allProductCodes.map((productCode) => selectTargetSubtotal(state, productCode));
  const targeTotalPrice = PriceUtils.volatileSum(...targetTotalsPerProduct);

  const weights = packEntries.map((entry: LineItem) => WeightUtils.multiply(entry.weight, entry.quantity));
  const weight = WeightUtils.sum(...weights);

  let quoteContainsOptimeProduct = false;

  Object.values(products.basic).forEach((singleBasicInfo) => {
    if (singleBasicInfo.isOptime) {
      quoteContainsOptimeProduct = true;
    }
  });

  const showOptimePopupOnCheckout = selectShowOptimePopupOnCheckout(state);

  return {
    accountNumber: cartPage.accountNumber,
    totalWeight: weight,
    totalSum: totalPrice,
    totalTargetSum: targeTotalPrice,
    entryIds: cart.cartEntries,
    validCart: packEntries.reduce((isValid, entry) => isValid && entry.valid, true as boolean),
    requestInFlight: cartPage.requestInFlight,
    dstError: cartPage.dstError,
    showOptimePopupOnCheckout,
  };
};

const mapDispatchToProps = (dispatch: CustomDispatch<RootState>): DispatchProps => {
  return {
    updateDate: (date: Date, packageEntryId: number) => dispatch(updateDate(date, packageEntryId)),
    deleteAll: () => dispatch(clearQuote()),
  };
};

// TODO: Wait for https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363 to be fixed
// @ts-ignore
const connected = connect(mapStateToProps, mapDispatchToProps)(ActiveQuoteEdit);
export default connected;
